'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

interface CompareRow {
  feature: string;
  movio: string | boolean;
  custom: string | boolean;
  competitor: string | boolean;
  highlight?: boolean;
}

const compareRows: CompareRow[] = [
  { feature: 'Time to Launch', movio: '2–4 weeks', custom: '6–18 months', competitor: '4–8 weeks', highlight: true },
  { feature: 'Upfront Cost', movio: 'Minimal setup cost', custom: 'Very high investment', competitor: 'High setup fee' },
  { feature: 'White-label Branding', movio: true, custom: true, competitor: false },
  { feature: 'Android + iOS Apps', movio: true, custom: true, competitor: true },
  { feature: 'AI Route Optimization', movio: true, custom: false, competitor: false, highlight: true },
  { feature: 'Multi-vertical (9 products)', movio: true, custom: false, competitor: false },
  { feature: 'Multi-city Franchise', movio: true, custom: false, competitor: false },
  { feature: 'WhatsApp Booking', movio: true, custom: false, competitor: false },
  { feature: 'Tier 2/3 City Support', movio: true, custom: true, competitor: false, highlight: true },
  { feature: 'Pay-as-you-grow Pricing', movio: true, custom: false, competitor: false },
  { feature: 'Dedicated Onboarding', movio: true, custom: true, competitor: false },
  { feature: 'Ongoing Updates', movio: true, custom: false, competitor: true },
  { feature: 'Source Code Ownership', movio: false, custom: true, competitor: false },
  { feature: 'India-specific Compliance', movio: true, custom: true, competitor: false },
];

const renderCell = (value: string | boolean, isMovio = false) => {
  if (typeof value === 'boolean') {
    return value ? (
      <Icon name="CheckCircleIcon" size={20} className={isMovio ? 'text-emerald-500' : 'text-emerald-400'} />
    ) : (
      <Icon name="XCircleIcon" size={20} className="text-red-400" />
    );
  }
  return <span className={`text-sm font-medium ${isMovio ? 'text-[#0F172A]' : 'text-[#475569]'}`}>{value}</span>;
};

export default function CompareContent() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="pt-24" aria-label="Platform comparison">
      {/* Hero */}
      <PageHero className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              Platform Comparison
            </span>
            <h1 className="font-display text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
              Why Movio{' '}
              <span className="italic font-light text-white/80">Wins.</span>
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto leading-relaxed animate-on-scroll stagger-2">
              Compare Movio against custom development and generic competitors. See the difference.
            </p>
          </div>
        </div>
      </PageHero>

      {/* Comparison Table */}
      <div className="bg-[#FFFFFF] py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 overflow-x-auto rounded-2xl border border-[rgba(15,23,42,0.08)] shadow-sm">
            <table className="w-full" role="table" aria-label="Platform feature comparison">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.08)]">
                  <th className="text-left p-6 text-sm font-semibold text-[#64748B] uppercase tracking-wider w-1/3" scope="col">
                    Feature
                  </th>
                  <th className="p-6 text-center w-1/5" scope="col">
                    <div className="inline-flex flex-col items-center gap-1">
                      <span className="text-base font-bold text-[#0066FF]">Movio</span>
                      <span className="text-xs text-emerald-500 font-medium bg-emerald-50 px-2 py-0.5 rounded-full">Recommended</span>
                    </div>
                  </th>
                  <th className="p-6 text-center w-1/5 text-sm font-semibold text-[#475569]" scope="col">Custom Dev</th>
                  <th className="p-6 text-center w-1/5 text-sm font-semibold text-[#475569]" scope="col">Generic SaaS</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-[rgba(15,23,42,0.05)] last:border-0 ${row.highlight ? 'bg-[rgba(0, 102, 255,0.03)]' : 'bg-white'}`}
                  >
                    <td className="p-5 text-sm font-medium text-[#0F172A]">{row.feature}</td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center">{renderCell(row.movio, true)}</div>
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center">{renderCell(row.custom)}</div>
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center">{renderCell(row.competitor)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Key advantages */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: 'BoltIcon', title: '10x Faster Launch', desc: 'Go live in 2–4 weeks instead of 6–18 months with custom development.' },
              { icon: 'CurrencyRupeeIcon', title: 'Significantly Lower Cost', desc: 'Fraction of what custom development costs. Pay-as-you-grow model that scales with your business.' },
              { icon: 'ArrowPathIcon', title: 'Continuous Updates', desc: 'New features, AI improvements, and compliance updates included.' },
            ].map((item, idx) => (
              <div
                key={item.title}
                className={`animate-on-scroll stagger-${idx + 2} bg-white rounded-2xl p-8 border border-[rgba(15,23,42,0.08)] shadow-sm text-center`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, rgba(0, 102, 255,0.12), rgba(7, 93, 223,0.08))' }}>
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={24} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="py-20 bg-white" aria-label="Get started">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <h2 className="font-display text-4xl font-semibold text-[#0F172A] mb-4">
              Ready to see it in action?
            </h2>
            <p className="text-[#475569] text-lg mb-8">
              Book a free demo and see how Movio can launch your business in weeks, not months.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/demo-booking" className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold">
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link href="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-medium text-[#0066FF] border border-[rgba(0, 102, 255,0.3)] hover:bg-[rgba(0, 102, 255,0.05)] transition-all">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}